import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from "@libs/zapi-ui/shadcn";
import { CaretDownIcon, FileDocIcon, FileMdIcon, FilePdfIcon, FilePptIcon } from "@phosphor-icons/react";
import { exportProject } from "../lib/pdf-export";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { usePresentationStore } from "@/zapi-frontend/stores/presentation";
import { AppButton, Typography } from "@libs/zapi-ui/custom";
import { ExportOptionsModal } from "./_ExportOptionsModal";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import $api from "@/zapi-frontend/shared/api/axios";
import { useQueryClient } from "@tanstack/react-query";

export function ExportButton() {
  const [searchParams] = useSearchParams();
  const isPresentationTab = searchParams.get("tab") === "presentation";
  const queryClient = useQueryClient();

  const { selectedProject, isFetchProjectLoading } = useProjectStore();
  const { presentation } = usePresentationStore();
  const { isLabs } = useMeStore();
  const [isExporting, setIsExporting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<"pdf" | "docx" | "md" | null>(null);

  const handleExport = async (
    docType: "pdf" | "docx" | "md",
    options: { includeAnalysisQuestions: boolean; includeIntelligenceQuestions: boolean }
  ) => {
    try {
      setIsExporting(true);
      await exportProject(selectedProject?.name || "", selectedProject?.id as unknown as number, docType, options);

      await queryClient.invalidateQueries({
        queryKey: ["exportHistory", selectedProject?.id.toString()]
      });

      toast.success("Document exported successfully");
      setModalOpen(false);
    } catch (error) {
      toast.error("Failed to export document");
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  const openExportModal = (docType: "pdf" | "docx" | "md") => {
    setSelectedDocType(docType);
    setModalOpen(true);
  };

  const handleExportPdf = () => openExportModal("pdf");
  const handleExportDocx = () => openExportModal("docx");
  const handleExportMd = () => openExportModal("md");

  const handleDownloadPresentationPdf = async () => {
    if (!presentation?.gammaPdfUrl || !selectedProject?.id) {
      toast.error("PDF not available");
      return;
    }
    await downloadFile("pdf");
  };

  const handleDownloadPresentationPptx = async () => {
    if (!presentation?.gammaPptxUrl || !selectedProject?.id) {
      toast.error("PPTX not available");
      return;
    }
    await downloadFile("pptx");
  };

  const downloadFile = async (fileType: "pdf" | "pptx") => {
    if (!selectedProject?.id) {
      toast.error("Project not available");
      return;
    }

    try {
      const response = await $api.get<{ data: { url: string } }>(
        `/projects/${selectedProject.id}/presentations/download-url`,
        {
          params: { fileType }
        }
      );

      const signedUrl = response.data.data.url;
      window.open(signedUrl, "_blank");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download file");
    }
  };

  const hasPresentationFiles = presentation?.gammaPdfUrl || presentation?.gammaPptxUrl;

  if (isPresentationTab) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild data-testid="export-menu-trigger">
          <AppButton
            title="Export Presentation"
            appearance="text"
            color="zinc100"
            disabled={!hasPresentationFiles || isFetchProjectLoading}
            endAdornment={<CaretDownIcon className="size-3" />}
          >
            <Typography variant="label2" color="zinc700">
              Export
            </Typography>
          </AppButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="px-1 py-2 border-innoveraZinc-100">
          <DropdownMenuLabel className="px-3">
            <Typography variant="label3" color="zinc500">
              Export Format
            </Typography>
          </DropdownMenuLabel>
          {presentation?.gammaPdfUrl && (
            <DropdownMenuItem
              data-testid="export-menu-presentation-pdf"
              onClick={handleDownloadPresentationPdf}
              className="px-3 py-2 flex items-center gap-1"
            >
              <FilePdfIcon color="var(--innovera-zinc900)" />
              <Typography variant="label2">PDF</Typography>
            </DropdownMenuItem>
          )}
          {presentation?.gammaPptxUrl && (
            <DropdownMenuItem
              data-testid="export-menu-presentation-pptx"
              onClick={handleDownloadPresentationPptx}
              className="px-3 py-2 flex items-center gap-1"
            >
              <FilePptIcon color="var(--innovera-zinc900)" />
              <Typography variant="label2">PPTX</Typography>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild data-testid="export-menu-trigger">
          <AppButton
            title="Export Project"
            appearance="text"
            color="zinc100"
            disabled={isExporting || isFetchProjectLoading}
            endAdornment={<CaretDownIcon className="size-3" />}
          >
            <Typography variant="label2" color="zinc700">
              Export
            </Typography>
          </AppButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="px-1 py-2 border-innoveraZinc-100">
          <DropdownMenuLabel className="px-3">
            <Typography variant="label3" color="zinc500">
              Export Format
            </Typography>
          </DropdownMenuLabel>
          <DropdownMenuItem
            data-testid="export-menu-pdf"
            onClick={handleExportPdf}
            className="px-3 py-2 flex items-center gap-1"
          >
            <FilePdfIcon color="var(--innovera-zinc900)" />
            <Typography variant="label2">PDF</Typography>
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="export-menu-docx"
            onClick={handleExportDocx}
            className="px-3 py-2 flex items-center gap-1"
          >
            <FileDocIcon color="var(--innovera-zinc900)" />
            <Typography variant="label2">DOCX</Typography>
          </DropdownMenuItem>
          {isLabs && (
            <DropdownMenuItem
              data-testid="export-menu-md"
              onClick={handleExportMd}
              className="px-3 py-2 flex items-center gap-1"
            >
              <FileMdIcon color="var(--innovera-zinc900)" />
              <Typography variant="label2">MD</Typography>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {selectedDocType && (
        <ExportOptionsModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onExport={(options) => handleExport(selectedDocType, options)}
          docType={selectedDocType}
          isExporting={isExporting}
        />
      )}
    </>
  );
}
